import { formatUkAndUtc } from './format-date';
import type { NextQuestionDayView } from './home-feed-view';

/** An instant for copy: UK local time with the UTC time. */
function at(iso: string): string {
    const when = formatUkAndUtc(iso);
    return `${when.uk} (${when.utc})`;
}

/** Plain-language state of today's Question card: one distinct sentence per reason the feed can give. */
export function todayReasonCopy(
    reason: string,
    until: string | null,
    snoozedUntil: string | null = null,
): string {
    switch (reason) {
        case 'OFFERED':
            return 'A question is offered today.';
        case 'NOT_A_QUESTION_DAY':
            return 'Not a question day for this member. The day is hashed from the Search, the member and the UTC date; about two days in three are not.';
        case 'SNOOZED':
            // `until` is the later of the snooze end and six hours after the last answer.
            if (until && snoozedUntil && Date.parse(until) > Date.parse(snoozedUntil)) {
                return `Snoozed with "Not now" until ${at(snoozedUntil)}; the quiet time after an answer runs past it, until ${at(until)}.`;
            }
            return until ? `Snoozed with "Not now" until ${at(until)}.` : 'Snoozed with "Not now".';
        case 'QUIET_AFTER_ANSWER':
            return until
                ? `Quiet for six hours after an answer, until ${at(until)}.`
                : 'Quiet for six hours after an answer.';
        case 'SCORING_WITHHELD':
            return 'Scoring is withheld for this household, or the Search could not be read, so there is no measure of what an answer would reorder and no question is asked.';
        case 'SEARCH_NOT_COVERED':
            return 'The index does not cover this Search (no areas, areas it does not serve, or renting), so there are no matches to ask about.';
        case 'NOTHING_WORTH_ASKING':
            return 'The timing allows a question but there is none worth asking: no homes, nothing left to ask, or no question that would change the order.';
        default:
            return reason;
    }
}

/** UTC calendar days from `evaluatedAt`'s UTC day to a `YYYY-MM-DD` day. */
function daysFromToday(evaluatedAt: string, day: string): number {
    // The UTC calendar day of the instant, whatever the browser's time zone.
    const today = Date.parse(new Date(evaluatedAt).toISOString().slice(0, 10));
    const target = Date.parse(day);
    return Math.round((target - today) / 86_400_000);
}

/** The next-question-day countdown: today / tomorrow / in N days / none within N days. */
export function nextDayCopy(
    nextDay: NextQuestionDayView | null,
    scanDays: number,
    evaluatedAt: string,
): string {
    if (!nextDay) return `No question day within the next ${scanDays} days.`;
    const days = daysFromToday(evaluatedAt, nextDay.day);
    if (days <= 0) {
        return Date.parse(nextDay.from) > Date.parse(evaluatedAt)
            ? `Today, from ${at(nextDay.from)}.`
            : 'Today.';
    }
    return days === 1 ? `Tomorrow (${nextDay.day} UTC).` : `In ${days} days (${nextDay.day} UTC).`;
}

/** Where the card would sit on the next question day. */
export function slotCopy(homesBefore: number): string {
    return homesBefore === 1 ? 'After 1 home.' : `After ${homesBefore} homes.`;
}

export const FAIR_SHARE_COPY =
    'A fair-share day: a firmness or "what kind" question is preferred over a ranking one.';

export function questionKindLabel(kind: string): string {
    switch (kind) {
        case 'PRIORITY':
            return 'Ranking question (priority)';
        case 'KEY_PLACE':
            return 'Ranking question (key place)';
        case 'FIRMNESS':
            return 'Firmness question';
        case 'WHAT_KIND':
            return '"What kind" question';
        default:
            return kind;
    }
}

/** The last "Not now": still running, or ended. */
export function snoozeCopy(snoozedUntil: string | null, evaluatedAt: string): string {
    if (!snoozedUntil) return 'Never snoozed.';
    return Date.parse(snoozedUntil) > Date.parse(evaluatedAt)
        ? `Last "Not now" ends ${at(snoozedUntil)}.`
        : `Last "Not now" ended ${at(snoozedUntil)}.`;
}

export function atCopy(iso: string): string {
    return at(iso);
}
